import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Save, ArrowLeft, Eye, EyeOff, ExternalLink, Plus, Trash2, Link as LinkIcon, ChevronUp, ChevronDown, Copy, History, Clock, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const F = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
    <div className="mt-1.5">{children}</div>
  </label>
);
const I = (p) => <input {...p} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />;
const TA = (p) => <textarea {...p} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />;
const uuid = () => crypto.randomUUID();

// Default `data` for each section type (schema-light defaults)
const SECTION_DEFAULTS = {
  hero: { badge: "", headline: "Big headline", subheadline: "", highlight_word: "", subtitle: "", primary_cta: { label: "Get started", href: "#" }, secondary_cta: { label: "", href: "" } },
  feature_grid: { eyebrow: "Features", title: "Why choose us", description: "", cards: [{ id: uuid(), icon: "Sparkles", title: "Feature", description: "Describe this benefit…", pills: [], is_visible: true }] },
  rich_text: { eyebrow: "", title: "", html: "<p>Your rich text content here…</p>" },
  cta_banner: { eyebrow: "", title: "Ready to get started?", description: "Join 50+ institutes already partnering with us.", cta: { label: "Partner with us", href: "/#contact" } },
  faq: { eyebrow: "FAQ", title: "Frequently asked questions", items: [{ q: "How fast do you deliver?", a: "Most content is delivered within 48 hours." }] },
  logo_grid: { eyebrow: "Trusted by", title: "Our partners", logos: [] },
  timeline: { eyebrow: "Our Journey", title: "Building the future", milestones: [] },
  testimonials: { eyebrow: "Testimonials", title: "Loved by teams", items: [] },
  pricing: { eyebrow: "Pricing", title: "Plans", description: "", tiers: [] },
  image_text: { eyebrow: "", title: "", html: "<p>Describe this feature…</p>", image_url: "", image_alt: "", image_left: true },
  stats_bar: { stats: [{ num: "50+", label: "Clients" }] },
  contact_form: { eyebrow: "Get in touch", title: "Let's talk.", description: "Fill the form and we'll be in touch." },
};

const SECTION_LABELS = {
  hero: "Hero", feature_grid: "Feature Grid", rich_text: "Rich Text", cta_banner: "CTA Banner",
  faq: "FAQ", logo_grid: "Logo Grid", timeline: "Timeline", testimonials: "Testimonials",
  pricing: "Pricing", image_text: "Image + Text", stats_bar: "Stats Bar", contact_form: "Contact Form",
};

// Per-type form
const SectionForm = ({ type, data, onChange }) => {
  const set = (k, v) => onChange({ ...data, [k]: v });
  switch (type) {
    case "hero":
      return (
        <div className="space-y-3">
          <F label="Badge"><I value={data.badge || ""} onChange={(e) => set("badge", e.target.value)} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Headline"><I value={data.headline || ""} onChange={(e) => set("headline", e.target.value)} /></F>
            <F label="Subheadline (second line)"><I value={data.subheadline || ""} onChange={(e) => set("subheadline", e.target.value)} /></F>
          </div>
          <F label="Highlight word (gets mint color)"><I value={data.highlight_word || ""} onChange={(e) => set("highlight_word", e.target.value)} /></F>
          <F label="Subtitle"><TA rows={3} value={data.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} /></F>
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-slate-200 rounded-lg p-3">
              <p className="text-xs font-semibold mb-2">PRIMARY CTA</p>
              <I placeholder="Label" value={data.primary_cta?.label || ""} onChange={(e) => set("primary_cta", { ...data.primary_cta, label: e.target.value })} />
              <I placeholder="Href" className="mt-2" value={data.primary_cta?.href || ""} onChange={(e) => set("primary_cta", { ...data.primary_cta, href: e.target.value })} />
            </div>
            <div className="border border-slate-200 rounded-lg p-3">
              <p className="text-xs font-semibold mb-2">SECONDARY CTA</p>
              <I placeholder="Label" value={data.secondary_cta?.label || ""} onChange={(e) => set("secondary_cta", { ...data.secondary_cta, label: e.target.value })} />
              <I placeholder="Href" className="mt-2" value={data.secondary_cta?.href || ""} onChange={(e) => set("secondary_cta", { ...data.secondary_cta, href: e.target.value })} />
            </div>
          </div>
        </div>
      );
    case "feature_grid":
      return (
        <div className="space-y-3">
          <F label="Eyebrow"><I value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></F>
          <F label="Title (HTML allowed)"><TA rows={2} value={data.title || ""} onChange={(e) => set("title", e.target.value)} /></F>
          <F label="Description"><TA rows={2} value={data.description || ""} onChange={(e) => set("description", e.target.value)} /></F>
          <div className="space-y-2">
            {(data.cards || []).map((c, i) => (
              <div key={c.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
                <div className="flex gap-2">
                  <I placeholder="Icon" value={c.icon || ""} onChange={(e) => { const next = [...data.cards]; next[i] = { ...c, icon: e.target.value }; set("cards", next); }} />
                  <I placeholder="Title" value={c.title || ""} onChange={(e) => { const next = [...data.cards]; next[i] = { ...c, title: e.target.value }; set("cards", next); }} />
                  <button onClick={() => set("cards", data.cards.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-50 px-2 rounded"><Trash2 size={14} /></button>
                </div>
                <TA rows={2} placeholder="Description" value={c.description || ""} onChange={(e) => { const next = [...data.cards]; next[i] = { ...c, description: e.target.value }; set("cards", next); }} />
                <I placeholder="Pills (comma-separated)" value={(c.pills || []).join(", ")} onChange={(e) => { const next = [...data.cards]; next[i] = { ...c, pills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }; set("cards", next); }} />
              </div>
            ))}
            <button onClick={() => set("cards", [...(data.cards || []), { id: uuid(), icon: "Sparkles", title: "New card", description: "", pills: [], is_visible: true }])} className="text-xs text-prepify-700 hover:underline">+ Add card</button>
          </div>
        </div>
      );
    case "rich_text":
      return (
        <div className="space-y-3">
          <F label="Eyebrow"><I value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></F>
          <F label="Title"><I value={data.title || ""} onChange={(e) => set("title", e.target.value)} /></F>
          <F label="HTML Content"><TA rows={14} className="font-mono text-[13px]" value={data.html || ""} onChange={(e) => set("html", e.target.value)} /></F>
        </div>
      );
    case "cta_banner":
      return (
        <div className="space-y-3">
          <F label="Eyebrow"><I value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></F>
          <F label="Title"><TA rows={2} value={data.title || ""} onChange={(e) => set("title", e.target.value)} /></F>
          <F label="Description"><TA rows={2} value={data.description || ""} onChange={(e) => set("description", e.target.value)} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Button Label"><I value={data.cta?.label || ""} onChange={(e) => set("cta", { ...data.cta, label: e.target.value })} /></F>
            <F label="Button Href"><I value={data.cta?.href || ""} onChange={(e) => set("cta", { ...data.cta, href: e.target.value })} /></F>
          </div>
        </div>
      );
    case "faq":
      return (
        <div className="space-y-3">
          <F label="Eyebrow"><I value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></F>
          <F label="Title"><I value={data.title || ""} onChange={(e) => set("title", e.target.value)} /></F>
          <div className="space-y-2">
            {(data.items || []).map((it, i) => (
              <div key={it._uid || `faq-${i}`} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
                <div className="flex gap-2">
                  <I placeholder="Question" value={it.q} onChange={(e) => { const next = [...data.items]; next[i] = { ...it, q: e.target.value }; set("items", next); }} />
                  <button onClick={() => set("items", data.items.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-50 px-2 rounded"><Trash2 size={14} /></button>
                </div>
                <TA rows={3} placeholder="Answer (HTML allowed)" value={it.a} onChange={(e) => { const next = [...data.items]; next[i] = { ...it, a: e.target.value }; set("items", next); }} />
              </div>
            ))}
            <button onClick={() => set("items", [...(data.items || []), { _uid: uuid(), q: "", a: "" }])} className="text-xs text-prepify-700 hover:underline">+ Add Q&A</button>
          </div>
        </div>
      );
    case "image_text":
      return (
        <div className="space-y-3">
          <F label="Eyebrow"><I value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></F>
          <F label="Title"><I value={data.title || ""} onChange={(e) => set("title", e.target.value)} /></F>
          <F label="HTML Body"><TA rows={6} className="font-mono text-[13px]" value={data.html || ""} onChange={(e) => set("html", e.target.value)} /></F>
          <F label="Image URL"><I value={data.image_url || ""} onChange={(e) => set("image_url", e.target.value)} /></F>
          <F label="Image Alt"><I value={data.image_alt || ""} onChange={(e) => set("image_alt", e.target.value)} /></F>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={data.image_left !== false} onChange={(e) => set("image_left", e.target.checked)} /> Image on left</label>
        </div>
      );
    case "stats_bar":
      return (
        <div className="space-y-2">
          {(data.stats || []).map((s, i) => (
            <div key={s._uid || `stat-${i}`} className="flex gap-2">
              <I placeholder="50+" value={s.num} onChange={(e) => { const next = [...data.stats]; next[i] = { ...s, num: e.target.value }; set("stats", next); }} />
              <I placeholder="Clients" value={s.label} onChange={(e) => { const next = [...data.stats]; next[i] = { ...s, label: e.target.value }; set("stats", next); }} />
              <button onClick={() => set("stats", data.stats.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-50 px-2 rounded"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => set("stats", [...(data.stats || []), { num: "", label: "" }])} className="text-xs text-prepify-700 hover:underline">+ Add stat</button>
        </div>
      );
    case "logo_grid":
      return (
        <div className="space-y-3">
          <F label="Eyebrow"><I value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></F>
          <F label="Title"><I value={data.title || ""} onChange={(e) => set("title", e.target.value)} /></F>
          {(data.logos || []).map((l, i) => (
            <div key={l._uid || `logo-${i}`} className="flex gap-2">
              <I placeholder="Name" value={l.name} onChange={(e) => { const next = [...data.logos]; next[i] = { ...l, name: e.target.value }; set("logos", next); }} />
              <I placeholder="Logo URL" value={l.logo_url || ""} onChange={(e) => { const next = [...data.logos]; next[i] = { ...l, logo_url: e.target.value }; set("logos", next); }} />
              <button onClick={() => set("logos", data.logos.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-50 px-2 rounded"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => set("logos", [...(data.logos || []), { _uid: uuid(), name: "", logo_url: "" }])} className="text-xs text-prepify-700 hover:underline">+ Add logo</button>
        </div>
      );
    case "testimonials":
      return (
        <div className="space-y-3">
          <F label="Eyebrow"><I value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></F>
          <F label="Title"><I value={data.title || ""} onChange={(e) => set("title", e.target.value)} /></F>
          {(data.items || []).map((t, i) => (
            <div key={t._uid || `test-${i}`} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
              <div className="flex justify-end"><button onClick={() => set("items", data.items.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-50 px-2 rounded"><Trash2 size={14} /></button></div>
              <TA rows={3} placeholder="Quote" value={t.quote} onChange={(e) => { const next = [...data.items]; next[i] = { ...t, quote: e.target.value }; set("items", next); }} />
              <div className="grid grid-cols-3 gap-2">
                <I placeholder="Name" value={t.name} onChange={(e) => { const next = [...data.items]; next[i] = { ...t, name: e.target.value }; set("items", next); }} />
                <I placeholder="Org" value={t.org} onChange={(e) => { const next = [...data.items]; next[i] = { ...t, org: e.target.value }; set("items", next); }} />
                <I placeholder="Avatar" value={t.avatar_initial || ""} onChange={(e) => { const next = [...data.items]; next[i] = { ...t, avatar_initial: e.target.value }; set("items", next); }} />
              </div>
            </div>
          ))}
          <button onClick={() => set("items", [...(data.items || []), { _uid: uuid(), quote: "", name: "", org: "", avatar_initial: "" }])} className="text-xs text-prepify-700 hover:underline">+ Add testimonial</button>
        </div>
      );
    case "contact_form":
      return (
        <div className="space-y-3">
          <F label="Eyebrow"><I value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></F>
          <F label="Title"><TA rows={2} value={data.title || ""} onChange={(e) => set("title", e.target.value)} /></F>
          <F label="Description"><TA rows={2} value={data.description || ""} onChange={(e) => set("description", e.target.value)} /></F>
          <p className="text-xs text-slate-500">This form submits to your CRM Leads automatically.</p>
        </div>
      );
    default:
      return <div className="text-sm text-slate-500">This section type has no editable fields yet.</div>;
  }
};

const PageEditor = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [sectionTypes, setSectionTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cms/pages/${id}`).then((r) => r.data),
      axios.get(`${API}/cms/section-types`).then((r) => r.data),
    ]).then(([p, t]) => { setPage(p); setSectionTypes(t); }).catch(() => { toast.error("Page not found"); nav("/app/website/pages"); });
  }, [id]);

  const loadRevisions = async () => {
    try {
      const res = await axios.get(`${API}/cms/pages/${id}/revisions`);
      setRevisions(res.data);
    } catch { toast.error("Could not load revisions"); }
  };

  const restoreRevision = async (revId) => {
    if (!window.confirm("Restore this revision? Current state will be saved as a new revision first.")) return;
    try {
      await axios.post(`${API}/cms/pages/${id}/revisions/${revId}/restore`);
      const res = await axios.get(`${API}/cms/pages/${id}`);
      setPage(res.data);
      toast.success("Restored");
      setShowRevisions(false);
      loadRevisions();
    } catch { toast.error("Restore failed"); }
  };

  const schedulePublish = async () => {
    if (!scheduleAt) { toast.error("Pick a date/time first"); return; }
    try {
      const iso = new Date(scheduleAt).toISOString();
      await axios.post(`${API}/cms/pages/${id}/schedule`, { when: iso });
      const res = await axios.get(`${API}/cms/pages/${id}`);
      setPage(res.data);
      setShowSchedule(false);
      toast.success(`Scheduled for ${new Date(iso).toLocaleString()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Schedule failed"); }
  };

  const unschedule = async () => {
    await axios.post(`${API}/cms/pages/${id}/unschedule`);
    const res = await axios.get(`${API}/cms/pages/${id}`);
    setPage(res.data);
    toast.success("Unscheduled");
  };

  if (!page) return <div className="text-slate-400">Loading…</div>;

  const patch = (k, v) => setPage({ ...page, [k]: v });
  const patchSEO = (k, v) => setPage({ ...page, seo: { ...page.seo, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/cms/pages/${id}`, {
        title: page.title, slug: page.slug, description: page.description,
        seo: page.seo, sections: page.sections,
      });
      setPage(res.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addSection = (type) => {
    const newSection = { id: uuid(), type, is_visible: true, order: page.sections.length, data: SECTION_DEFAULTS[type] || {} };
    patch("sections", [...page.sections, newSection]);
    setShowPicker(false);
  };

  const moveSection = (idx, dir) => {
    const next = [...page.sections];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    patch("sections", next.map((s, i) => ({ ...s, order: i })));
  };

  const duplicateSection = (idx) => {
    const s = page.sections[idx];
    const copy = { ...s, id: uuid(), data: JSON.parse(JSON.stringify(s.data)) };
    const next = [...page.sections.slice(0, idx + 1), copy, ...page.sections.slice(idx + 1)];
    patch("sections", next.map((s, i) => ({ ...s, order: i })));
  };

  const removeSection = (idx) => {
    if (!window.confirm("Remove this section?")) return;
    patch("sections", page.sections.filter((_, i) => i !== idx));
  };

  const updateSectionData = (idx, newData) => {
    const next = [...page.sections];
    next[idx] = { ...next[idx], data: newData };
    patch("sections", next);
  };

  const toggleVisible = (idx) => {
    const next = [...page.sections];
    next[idx] = { ...next[idx], is_visible: !next[idx].is_visible };
    patch("sections", next);
  };

  const togglePublish = async () => {
    const ep = page.status === "published" ? "unpublish" : "publish";
    await axios.post(`${API}/cms/pages/${id}/${ep}`);
    const res = await axios.get(`${API}/cms/pages/${id}`);
    setPage(res.data);
    toast.success(page.status === "published" ? "Unpublished" : "Published");
  };

  const generatePreview = async () => {
    try {
      const res = await axios.post(`${API}/cms/pages/${id}/preview-token`);
      const url = `${BASE}/preview/${res.data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Preview URL copied to clipboard (valid 24h)");
      window.open(url, "_blank");
    } catch {
      toast.error("Could not generate preview");
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3 sticky top-0 bg-[#F8FAFC]/95 backdrop-blur py-3 z-10 -mx-6 px-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <Link to="/app/website/pages" className="text-slate-500 hover:text-slate-900 p-1"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{page.title || "Untitled"}</h1>
            <p className="text-xs text-slate-500">
              <span className={`inline-block mr-2 px-2 py-0.5 rounded-full ${page.status === "published" ? "bg-prepify-100 text-prepify-700" : "bg-slate-100 text-slate-600"}`}>{page.status}</span>
              <code>/{page.slug}</code>
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setShowRevisions(true); loadRevisions(); }} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg text-sm" data-testid="revisions-btn">
            <History size={14} /> Revisions
          </button>
          <button onClick={() => setShowSchedule(true)} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg text-sm" data-testid="schedule-btn">
            <Clock size={14} /> Schedule
          </button>
          <button onClick={generatePreview} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg text-sm" data-testid="preview-btn">
            <ExternalLink size={14} /> Preview
          </button>
          {page.status === "published" && (
            <a href={`${BASE}/${page.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg text-sm">
              <ExternalLink size={14} /> View live
            </a>
          )}
          <button onClick={togglePublish} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-4 py-2.5 rounded-lg text-sm" data-testid="toggle-publish-btn">
            {page.status === "published" ? <><EyeOff size={14} /> Unpublish</> : <><Eye size={14} /> Publish</>}
          </button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-prepify-600 text-white font-medium px-5 py-2.5 rounded-lg disabled:opacity-50" data-testid="save-page-btn">
            <Save size={16} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-semibold">Basics</h3>
            <F label="Title"><I value={page.title} onChange={(e) => patch("title", e.target.value)} /></F>
            <F label="Slug" hint="URL path, e.g. 'about-us'. Leave blank to auto-generate from title."><I value={page.slug || ""} onChange={(e) => patch("slug", e.target.value)} /></F>
            <F label="Short Description" hint="Shown in list view and as fallback meta description"><TA rows={2} value={page.description || ""} onChange={(e) => patch("description", e.target.value)} /></F>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Sections <span className="text-sm font-normal text-slate-400 ml-2">{page.sections.length} on page</span></h3>
              <button onClick={() => setShowPicker(!showPicker)} className="inline-flex items-center gap-2 bg-prepify-600 hover:bg-prepify-700 text-white font-medium px-4 py-2 rounded-lg text-sm" data-testid="add-section-btn">
                <Plus size={14} /> Add section
              </button>
            </div>

            {showPicker && (
              <div className="grid grid-cols-2 gap-2 mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                {sectionTypes.map((t) => (
                  <button key={t.type} onClick={() => addSection(t.type)} className="text-left border border-slate-200 bg-white hover:border-prepify-500 hover:bg-prepify-50 rounded-lg p-3 transition" data-testid={`pick-${t.type}`}>
                    <div className="font-medium text-sm text-slate-900">{t.label}</div>
                    <div className="text-xs text-slate-500">{t.description}</div>
                  </button>
                ))}
              </div>
            )}

            {page.sections.length === 0 ? (
              <div className="text-slate-400 text-center py-10 border-2 border-dashed border-slate-200 rounded-xl">
                No sections yet. Click <b>Add section</b> to pick from the library.
              </div>
            ) : (
              <div className="space-y-3">
                {page.sections.map((s, idx) => (
                  <details key={s.id} className="border border-slate-200 rounded-xl bg-slate-50/30 group" data-testid={`section-${s.id}`}>
                    <summary className="cursor-pointer list-none px-4 py-3 flex items-center gap-3 hover:bg-slate-50 rounded-xl">
                      <span className="text-xs font-mono bg-white border border-slate-200 rounded px-2 py-1">{String(idx + 1).padStart(2, "0")}</span>
                      <span className="font-medium text-sm flex-1">{SECTION_LABELS[s.type] || s.type}</span>
                      <button onClick={(e) => { e.preventDefault(); moveSection(idx, -1); }} disabled={idx === 0} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronUp size={14} /></button>
                      <button onClick={(e) => { e.preventDefault(); moveSection(idx, 1); }} disabled={idx === page.sections.length - 1} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronDown size={14} /></button>
                      <button onClick={(e) => { e.preventDefault(); toggleVisible(idx); }} className={`p-1 rounded ${s.is_visible ? "text-prepify-600" : "text-slate-400"}`}>{s.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                      <button onClick={(e) => { e.preventDefault(); duplicateSection(idx); }} className="p-1 hover:bg-slate-100 rounded"><Copy size={14} /></button>
                      <button onClick={(e) => { e.preventDefault(); removeSection(idx); }} className="p-1 hover:bg-red-50 text-red-500 rounded"><Trash2 size={14} /></button>
                    </summary>
                    <div className="px-4 pb-4 pt-2 border-t border-slate-200">
                      <SectionForm type={s.type} data={s.data} onChange={(newData) => updateSectionData(idx, newData)} />
                    </div>
                  </details>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><LinkIcon size={14} /> SEO</h3>
            <F label="SEO Title" hint="Defaults to page title"><I value={page.seo?.title || ""} onChange={(e) => patchSEO("title", e.target.value)} /></F>
            <F label="Meta Description"><TA rows={3} value={page.seo?.description || ""} onChange={(e) => patchSEO("description", e.target.value)} /></F>
            <F label="OG Image URL"><I value={page.seo?.og_image_url || ""} onChange={(e) => patchSEO("og_image_url", e.target.value)} /></F>
            <F label="Canonical URL"><I value={page.seo?.canonical_url || ""} onChange={(e) => patchSEO("canonical_url", e.target.value)} /></F>
            <F label="Robots">
              <select value={page.seo?.robots || "index,follow"} onChange={(e) => patchSEO("robots", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="index,follow">index, follow</option>
                <option value="noindex,nofollow">noindex, nofollow</option>
                <option value="noindex,follow">noindex, follow</option>
                <option value="index,nofollow">index, nofollow</option>
              </select>
            </F>
            <F label="Keywords (comma-separated)"><I value={(page.seo?.keywords || []).join(", ")} onChange={(e) => patchSEO("keywords", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></F>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Info</h3>
            <p className="text-xs text-slate-500">Updated: {new Date(page.updated_at).toLocaleString()}</p>
            {page.published_at && <p className="text-xs text-slate-500">Published: {new Date(page.published_at).toLocaleString()}</p>}
            {page.scheduled_publish_at && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800 font-medium flex items-center gap-1"><Clock size={12} /> Scheduled for {new Date(page.scheduled_publish_at).toLocaleString()}</p>
                <button onClick={unschedule} className="text-xs text-amber-700 hover:underline mt-1">Cancel schedule</button>
              </div>
            )}
          </section>
        </aside>
      </div>

      {/* Revisions Modal */}
      {showRevisions && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowRevisions(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-semibold">Revision History</h3>
              <button onClick={() => setShowRevisions(false)} className="text-slate-400 hover:text-slate-900">✕</button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[60vh]">
              {revisions.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No revisions yet. Every save creates one.</p>
              ) : (
                <div className="space-y-2">
                  {revisions.map((r) => (
                    <div key={r.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3 hover:bg-slate-50">
                      <div>
                        <p className="text-sm font-medium">{new Date(r.created_at).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">by {r.created_by_name || "Unknown"} · {r.note}</p>
                      </div>
                      <button onClick={() => restoreRevision(r.id)} className="text-xs bg-slate-900 hover:bg-prepify-600 text-white px-3 py-1.5 rounded inline-flex items-center gap-1" data-testid={`restore-${r.id}`}>
                        <RotateCcw size={12} /> Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showSchedule && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowSchedule(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-semibold">Schedule Publish</h3>
              <button onClick={() => setShowSchedule(false)} className="text-slate-400 hover:text-slate-900">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-500">Page will auto-publish at the selected time. The scheduler runs every minute.</p>
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                data-testid="schedule-datetime"
                min={new Date().toISOString().slice(0, 16)}
              />
              <div className="flex gap-2">
                <button onClick={() => setShowSchedule(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-700 py-2 rounded-lg text-sm">Cancel</button>
                <button onClick={schedulePublish} className="flex-1 bg-prepify-600 hover:bg-prepify-700 text-white font-medium py-2 rounded-lg text-sm" data-testid="confirm-schedule-btn">Schedule</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageEditor;
